"use client"

import { usePathname } from "next/navigation"
import { Section } from "@/components/layout/Section"
import { Button } from "@/components/ui/button"
import { Store, CheckCircle2, FileText, Calendar, Code2, Rocket, Mail, Phone, ArrowRight } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import { siteConfig } from "@/config/site"
import { CTA } from "@/components/sections/CTA"
import { ServiceReferences } from "@/components/sections/ServiceReferences"
import { type Locale } from "@/lib/i18n"

const metadata = {
  title: "Shopify Tasarım ve Geliştirme | Shopify Mağaza Tasarımı | LAK VERSE",
  description: "Shopify mağazanız için özel tema tasarımı ve geliştirme hizmetleri. Liquid template, Shopify app entegrasyonları, mobil uyumlu tasarım. Dönüşüm oranlarını artıran, SEO optimize edilmiş Shopify çözümleri.",
  keywords: "Shopify tasarım, Shopify geliştirme, Shopify tema, Shopify mağaza, Liquid template, Shopify app, Shopify custom theme, Shopify store design, Shopify optimization",
}

const features = [
  "Markanıza özel Shopify mağaza tasarımı",
  "Telefon, tablet ve bilgisayarda mükemmel görünüm",
  "Google'da üst sıralarda görünme",
  "Satışlarınızı artıran kullanıcı dostu tasarım",
  "Shopify uygulamaları ile entegrasyon",
  "Saniyeler içinde açılan hızlı mağazalar",
]

const steps = [
  {
    icon: FileText,
    title: "Teklif Al",
    description: "Projeniz için ücretsiz teklif alın ve ihtiyaçlarınızı paylaşın",
  },
  {
    icon: Calendar,
    title: "Planla",
    description: "Proje planını birlikte oluşturalım ve zaman çizelgesini belirleyelim",
  },
  {
    icon: Code2,
    title: "Geliştir",
    description: "Profesyonel ekibimiz projenizi geliştirirken sizi bilgilendiriyoruz",
  },
  {
    icon: Rocket,
    title: "Yayınla",
    description: "Projeniz hazır! Yayınlıyoruz ve destek sağlıyoruz",
  },
]

export default function ShopifyPage() {
  const pathname = usePathname()
  const currentLocale: Locale = pathname?.startsWith('/en/') ? 'en' : 'tr'
  
  return (
    <>
      <Section className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white py-12 sm:py-16 lg:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex p-4 bg-white/10 rounded-lg mb-6">
            <Store className="h-12 w-12" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl mb-6">
            Shopify Tasarım - Geliştirme
          </h1>
          <p className="text-lg text-white/90 sm:text-xl">
            Shopify mağazanız için özel tema tasarımı ve geliştirme hizmetleri
          </p>
        </div>
      </Section>

      <Section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto">
          {/* Görsel ve Açıklama */}
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-8">
              <div className="relative h-[300px] lg:h-[400px] bg-slate-100">
                <Image
                  src="/shopify.jpg"
                  alt="Shopify Tasarım - Geliştirme"
                  fill
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-[rgb(3,74,166)]/30"></div>
              </div>
              <div className="flex flex-col justify-center">
                <h2 className="text-3xl font-bold mb-6">Profesyonel Shopify Geliştirme Hizmetleri</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Shopify mağazanız için özel tema tasarımı ve geliştirme hizmetleri sunuyoruz. 
                  Dönüşüm oranlarını artıran, mobil uyumlu ve SEO optimize edilmiş Shopify 
                  çözümleri geliştiriyoruz. Markanızın kimliğine uygun, benzersiz ve profesyonel 
                  Shopify temaları oluşturuyoruz.
                </p>
                <p className="text-lg text-muted-foreground">
                  Modern teknolojiler kullanarak, mağazanızın ihtiyaçlarına özel çözümler geliştiriyoruz. 
                  Shopify uygulamaları ile entegrasyonlar, özel özellikler ve hızlı çalışma ile 
                  mağazanızı bir adım öne taşıyoruz.
                </p>
              </div>
            </div>
            <p className="text-lg text-muted-foreground">
              Markanıza özel tema tasarımı, gelişmiş özellikler ve diğer sistemlerle uyumlu 
              entegrasyonlar ekliyoruz. Shopify uygulamaları, özel ödeme süreçleri, ürün 
              özelleştirme seçenekleri ve müşteri yönetimi ile kapsamlı Shopify çözümleri sunuyoruz. 
              Hızlı çalışma, görsel optimizasyonu ve saniyeler içinde açılan sayfalar ile 
              mağazanızın satışlarını artırıyoruz. Büyük işletmeler için özel çözümler de sunuyoruz.
            </p>
          </div>

          {/* Özellikler */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold mb-6">Özellikler</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-[rgb(3,74,166)] mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Çalışma Aşamaları */}
          <div className="mb-12">
            <div>
              {/* Başlık */}
              <div className="text-center mb-12">
                <h3 className="text-3xl lg:text-4xl font-bold">
                  <span className="text-slate-900">Kanıtlanmış</span>{" "}
                  <span className="text-[rgb(3,74,166)]">Çalışma Sürecimiz</span>
                </h3>
              </div>

              {/* Aşamalar */}
              <div className="relative">
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
                  {steps.map((step, index) => {
                    const Icon = step.icon
                    const stepNumber = String(index + 1).padStart(2, '0')
                    return (
                      <div key={index} className="relative">
                        {/* Bağlantı çizgisi (son adım hariç) */}
                        {index < steps.length - 1 && (
                          <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-slate-300 -z-10" 
                               style={{ width: 'calc(100% - 80px)', left: 'calc(50% + 40px)' }} />
                        )}
                        
                        <div className="text-center">
                          {/* İkon ve Numara */}
                          <div className="relative inline-block mb-6">
                            {/* Mavi daire - İkon */}
                            <div className="w-16 h-16 rounded-full bg-[rgb(3,74,166)] flex items-center justify-center">
                              <Icon className="h-7 w-7 text-white" />
                            </div>
                            {/* Numara - Sağ üstte */}
                            <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-slate-700 border-2 border-white flex items-center justify-center">
                              <span className="text-white text-xs font-bold">{stepNumber}</span>
                            </div>
                          </div>
                          
                          {/* Başlık */}
                          <h4 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h4>
                          
                          {/* Açıklama */}
                          <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
      <ServiceReferences serviceSlug="shopify" />
      <CTA locale={currentLocale} />
    </>
  )
}

